import React, {useRef} from 'react';
import {useSwipeable} from 'react-swipeable';

export const ANIMATION_DURATION = 300;
const DRAG_TRANSITION_FUNCTION = 'cubic-bezier(0.81, 0.08, 0.68, 0.53)';
const DRAG_TRANSITION_FUNCTION_AND_TIME = `${ANIMATION_DURATION}ms ${DRAG_TRANSITION_FUNCTION}`;
export const CLICK_OUTSIDE_LAYOUT_SWIPABLE = false;

export type DraggableBottomsheetProps = {
  draggedPercentageToClose: number;
  onClose: VoidFunction;
  dialogRef: React.RefObject<HTMLElement | null>;
  headerRef: React.MutableRefObject<HTMLElement | null>;
};

export const useDraggableBottomsheet = ({
  onClose,
  draggedPercentageToClose,
  dialogRef,
  headerRef,
}: DraggableBottomsheetProps) => {
  const deltaYCorrection = React.useRef(-1);
  const deltaYPositionAfterSwiped = React.useRef(-1);
  const canSwipeOut = useRef(false);

  const {ref: swipableRef} = useSwipeable({
    preventDefaultTouchmoveEvent: false,
    onSwipeStart: () => {
      canSwipeOut.current = false;
    },
    onSwipedDown: () => {
      const shouldClose = getShouldClose({
        canSwipeOut,
        dialogRef,
        deltaYPositionAfterSwiped,
        draggedPercentageToClose,
      });

      if (shouldClose) {
        dialogRef.current?.style.setProperty(
          'transform',
          `translateY(${dialogRef.current.clientHeight}px)`,
        );
        dialogRef.current?.style.setProperty('opacity', '0');
        onClose();
      }
      deltaYPositionAfterSwiped.current = -1;
    },
    onSwiping: ({deltaY, event, dir}) => {
      event.stopPropagation();

      if (dir !== 'Down') {
        return null;
      }

      if (!canSwipeOut.current) {
        canSwipeOut.current = true;
      }

      if (deltaYCorrection.current === -1) {
        /**
         * Added a correction since delta can be different from the moment whe the user starts swiping
         * to the moment when the Flyout can be swiped down. This correction is only set once, when the
         * Flyout can be swiped down
         * */
        deltaYCorrection.current = deltaY;
      }

      const translateYPosition = deltaY - deltaYCorrection.current;

      dialogRef.current?.style.setProperty('transition', 'none');
      dialogRef.current?.style.setProperty(
        'transform',
        `translateY(max(${translateYPosition}px, 0px))`,
      );
    },
    onSwiped: ({deltaY}) => {
      if (deltaY > 0) {
        deltaYPositionAfterSwiped.current = deltaY - deltaYCorrection.current;
      }
      dialogRef.current?.style.setProperty(
        'transition',
        `transform ${DRAG_TRANSITION_FUNCTION_AND_TIME}, opacity ${DRAG_TRANSITION_FUNCTION_AND_TIME}`,
      );

      const shouldClose = getShouldClose({
        canSwipeOut,
        dialogRef,
        deltaYPositionAfterSwiped,
        draggedPercentageToClose,
      });

      if (!shouldClose) {
        dialogRef.current?.style.setProperty('transform', 'translateY(0)');
      }
      deltaYCorrection.current = -1;
    },
  });

  const passRefThrough = (el: HTMLElement | null) => {
    swipableRef(el);
    headerRef.current = el;
  };

  return {passRefThrough};
};

const getShouldClose = ({
  dialogRef,
  deltaYPositionAfterSwiped,
  canSwipeOut,
  draggedPercentageToClose,
}: {
  dialogRef: React.RefObject<HTMLElement | null>;
  deltaYPositionAfterSwiped: React.RefObject<number>;
  canSwipeOut: React.RefObject<boolean>;
  draggedPercentageToClose: DraggableBottomsheetProps['draggedPercentageToClose'];
}) => {
  const deltaY = deltaYPositionAfterSwiped.current ?? -1;

  return (
    canSwipeOut.current &&
    dialogRef.current &&
    deltaY > dialogRef.current.clientHeight * draggedPercentageToClose
  );
};
